import {useEffect, useId, useRef} from 'react'

import {type DitherParams, useWebGLContext} from '../WebGLContext/WebGLContext'

interface WebGLDitherProps {
  className?: string
  params: DitherParams
}

export function WebGLDither({className, params}: WebGLDitherProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isRegisteredRef = useRef(false)
  const instanceRef = useRef<number>(0)

  const {registerDither, unregisterDither} = useWebGLContext()
  const id = useId()

  const getInstanceId = () => `${id}-${instanceRef.current}`

  useEffect(() => {
    if (!ref.current) return
    if (isRegisteredRef.current) {
      unregisterDither(getInstanceId())
      isRegisteredRef.current = false
    }

    instanceRef.current++

    registerDither({id: getInstanceId(), element: ref.current, params})
    isRegisteredRef.current = true
    // Re-registration is deliberately keyed on the individual `params` fields:
    // the `params` object itself is a new literal every render, and
    // `getInstanceId` is redefined every render, so listing either as a dep
    // would re-register the dither on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.color, params.type, params.fadeIn, params.dotOpacity])

  useEffect(() => {
    return () => {
      if (isRegisteredRef.current) {
        unregisterDither(getInstanceId())
        isRegisteredRef.current = false
      }
    }
    // Unmount-only cleanup. `getInstanceId` is redefined every render, so
    // listing it would run this cleanup — unregistering the dither — on every
    // render. It only reads `id` and a ref, so the stale closure is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <div ref={ref} className={className} />
}
